import { useContext, createContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { axiosConfig } from "./axiosAPI";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("accessToken") || "");
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const navigate = useNavigate();
  
  // New function to fetch user permissions
  const fetchUserPermissions = async (token) => {
    setPermissionsLoading(true);
    try {
      const response = await axiosConfig.get(
        `${process.env.REACT_APP_BASE_URL}/users/my/permissions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      // Extract permissions from the nested structure
      const permissionsArray = response.data?.data?.permissions || [];
      setPermissions(permissionsArray);
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      setPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  // Re-fetch permissions on app mount if user has token
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken && permissions.length === 0) {
      fetchUserPermissions(accessToken);
    }
  }, []);

  const loginAction = async (data) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/auth/authenticate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
          body: JSON.stringify(data),
        }
      );
      const res = await response.json();

      if (response.ok && res.accessToken) {
        setUser(res.refreshToken);
        setToken(res.accessToken);
        localStorage.setItem("accessToken", res.accessToken);
        localStorage.setItem("refreshToken", res.refreshToken);

        await fetchUserPermissions(res.accessToken);

        await Swal.fire({
          icon: "success",
          title: "Success",
          text: "Login successful",
          showConfirmButton: false,
          timer: 1000,
          timerProgressBar: true,
        });

        navigate("/Dashboard");
        return;
      }

      throw new Error(res.title || res.message || "Invalid phone number or password");
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: err.message || "Something went wrong. Please try again.",
      });
    }
  };


  // Update logOut to clear permissions
  const logOut = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/auth/logout`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
            "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
          },
        }
      );
      if (response) {
        setUser(null);
        setToken("");
        setPermissions([]); // Clear permissions on logout
        localStorage.clear();
        navigate("/");
        return;
      }
      throw new Error(response.message);
    } catch (err) {
      console.error(err);
    }
  };

  // const logOut = () => {
    
  //   setUser(null);
  //   setToken("");
  //   localStorage.clear();
  //   navigate("/");
  // };

  return (
    <AuthContext.Provider value={{ 
      token, 
      user, 
      permissions, 
      permissionsLoading, 
      loginAction, 
      logOut 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
